import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { downloadTicketPdf } from "../lib/pdf";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

import { toast } from "sonner";
import { Ticket, ShoppingBag, DownloadSimple, Calendar, MapPin, Clock, ArrowLeft, ShieldCheck, Hash } from "@phosphor-icons/react";

/* ─── Digital Ticket View ───────────────────────────────────── */
function DigitalTicketView({ ticket, open, onClose }) {
  if (!ticket) return null;

  const eventDate = new Date(ticket.event_date);
  const isInvalid = isNaN(eventDate.getTime());
  const dateStr = isInvalid
    ? "TBD"
    : eventDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const timeStr = isInvalid
    ? "TBD"
    : eventDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  /* We ONLY use the ticket's stored event image URL. */
  const img = ticket.event_image_url || "https://placehold.co/600x300/1a1036/6366f1?text=Event";

  /* Gate is something like "Gate A", "Gate B", etc */
  const gate = ticket.gate || "General";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[420px] p-0 border-0 bg-transparent shadow-none overflow-hidden">
        <DialogTitle className="sr-only">Digital Ticket</DialogTitle>

        <div className="relative bg-[#0b0b0e] rounded-2xl border border-[#2a2a2f] shadow-2xl overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0">
            <button
              onClick={onClose}
              className="flex items-center gap-1 text-neutral-400 hover:text-white text-sm transition-colors"
            >
              <ArrowLeft size={16} /> Close
            </button>
            <button onClick={onClose} className="text-neutral-500 hover:text-white">
              <span className="sr-only">Close</span>×
            </button>
          </div>

          {/* ── Glow border: positioned OUTSIDE the scroll flow ── */}
          <div aria-hidden="true" className="pointer-events-none absolute z-0 ticket-glow-ring" />

          {/* Scrollable area with stable scrollbar */}
          <div className="relative z-10 px-3 pb-3 overflow-y-auto" style={{ maxHeight: '80vh', scrollbarWidth: 'thin', scrollbarColor: '#2a2a3d transparent' }}>

            {/* Ticket card */}
            <div className="relative bg-[#141420] rounded-2xl overflow-hidden border border-[#2a2a3d] shadow-lg" id="ticket-card-inner">

                {/* Event image */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={img}
                    alt={ticket.event_title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = "https://placehold.co/600x300/1a1036/6366f1?text=Event"; }}
                  />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#141420] to-transparent" />
                </div>

                {/* Body */}
                <div className="px-5 pt-3 pb-4">

                  {/* Ticket Number / Serial */}
                  <div className="flex justify-center mb-3">
                    <div className="flex items-center gap-1.5 text-xs font-mono text-neutral-400 bg-[#1f1f2e] border border-[#2a2a40] px-3 py-1 rounded-full">
                      <Hash size={12} className="text-[#7C3AED]" />
                      <span>{ticket.serial_number || `TICKET-${ticket.id.slice(0, 8).toUpperCase()}`}</span>
                    </div>
                  </div>

                  {/* Dark-mode "GENERAL" chevron */}
                  <div className="flex justify-center mb-4">
                    <div
                      className="text-center font-bold uppercase tracking-widest text-xs px-6 py-1.5"
                      style={{
                        background: "#1f1f2e",
                        border: "1px solid #2a2a40",
                        clipPath: "polygon(8% 0%, 92% 0%, 100% 50%, 92% 100%, 8% 100%, 0% 50%)",
                        color: "#a78bfa",
                      }}
                    >
                      {ticket.ticket_type_name}
                    </div>
                  </div>

                  {/* Event name */}
                  <h2 className="font-display text-2xl text-white text-center leading-tight tracking-tight mb-5">
                    {ticket.event_title}
                  </h2>

                  <TicketDivider />

                  {/* Info rows */}
                  <div className="space-y-3 my-5 text-sm">
                    <InfoRow icon={<Calendar size={18} weight="duotone" className="text-[#7C3AED]" />} label="Date" value={dateStr} />
                    <InfoRow icon={<Clock size={18} weight="duotone" className="text-[#7C3AED]" />} label="Time" value={timeStr} />
                    <InfoRow icon={<MapPin size={18} weight="duotone" className="text-[#7C3AED]" />} label="Venue" value={ticket.venue} />
                    <InfoRow icon={<Ticket size={18} weight="duotone" className="text-[#7C3AED]" />} label="Gate" value={gate} />
                    <InfoRow icon={<ShieldCheck size={18} weight="duotone" className="text-[#7C3AED]" />} label="Seat" value="General" />
                  </div>

                  <TicketDivider />

                  {/* Scan for entry */}
                  <div className="mt-5 mb-4 text-center">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-bold mb-3">
                      Scan for entry
                    </p>
                    <div className="inline-block bg-white rounded-xl p-2 shadow-lg">
                      <img
                        src={ticket.qr_data_url}
                        alt="QR Code"
                        className="w-36 h-36"
                      />
                    </div>
                  </div>

                  {/* Barcode */}
                  <div className="flex flex-col items-center my-4">
                    <svg className="w-full h-8 text-neutral-300" viewBox="0 0 200 40" fill="currentColor">
                      <rect x="0" y="0" width="2" height="40" />
                      <rect x="4" y="0" width="1" height="40" />
                      <rect x="7" y="0" width="3" height="40" />
                      <rect x="12" y="0" width="1" height="40" />
                      <rect x="15" y="0" width="2" height="40" />
                      <rect x="20" y="0" width="4" height="40" />
                      <rect x="26" y="0" width="1" height="40" />
                      <rect x="29" y="0" width="2" height="40" />
                      <rect x="33" y="0" width="3" height="40" />
                      <rect x="38" y="0" width="1" height="40" />
                      <rect x="42" y="0" width="2" height="40" />
                      <rect x="46" y="0" width="1" height="40" />
                      <rect x="49" y="0" width="4" height="40" />
                      <rect x="55" y="0" width="1" height="40" />
                      <rect x="58" y="0" width="2" height="40" />
                      <rect x="62" y="0" width="3" height="40" />
                      <rect x="67" y="0" width="1" height="40" />
                      <rect x="70" y="0" width="2" height="40" />
                      <rect x="75" y="0" width="1" height="40" />
                      <rect x="78" y="0" width="3" height="40" />
                      <rect x="83" y="0" width="2" height="40" />
                      <rect x="87" y="0" width="1" height="40" />
                      <rect x="91" y="0" width="2" height="40" />
                      <rect x="95" y="0" width="1" height="40" />
                      <rect x="99" y="0" width="4" height="40" />
                      <rect x="105" y="0" width="1" height="40" />
                      <rect x="108" y="0" width="2" height="40" />
                      <rect x="112" y="0" width="3" height="40" />
                      <rect x="117" y="0" width="1" height="40" />
                      <rect x="121" y="0" width="2" height="40" />
                      <rect x="125" y="0" width="1" height="40" />
                      <rect x="129" y="0" width="4" height="40" />
                      <rect x="135" y="0" width="1" height="40" />
                      <rect x="138" y="0" width="2" height="40" />
                      <rect x="142" y="0" width="3" height="40" />
                      <rect x="147" y="0" width="1" height="40" />
                      <rect x="151" y="0" width="2" height="40" />
                      <rect x="156" y="0" width="1" height="40" />
                      <rect x="159" y="0" width="3" height="40" />
                      <rect x="164" y="0" width="2" height="40" />
                      <rect x="168" y="0" width="1" height="40" />
                      <rect x="172" y="0" width="2" height="40" />
                      <rect x="176" y="0" width="4" height="40" />
                      <rect x="181" y="0" width="1" height="40" />
                      <rect x="185" y="0" width="3" height="40" />
                      <rect x="190" y="0" width="1" height="40" />
                      <rect x="194" y="0" width="2" height="40" />
                      <rect x="198" y="0" width="2" height="40" />
                    </svg>
                    <span className="text-[10px] text-neutral-500 font-mono mt-1">{ticket.serial_number || ticket.id}</span>
                  </div>

                  {/* Download button */}
                  <button
                    onClick={async () => {
                      const ok = await downloadTicketPdf(ticket.id, ticket.serial_number);
                      if (ok) toast.success("PDF downloaded"); else toast.error("Download failed");
                    }}
                    className="w-full mt-1 bg-gradient-to-r from-[#4F46E5] to-[#9333EA] text-white font-bold uppercase tracking-widest text-xs py-3 rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                  >
                    <DownloadSimple size={14} weight="bold" /> Download PDF
          </button>
              </div>
            </div>

            {/* Footer */}
            <div className="pb-3 pt-2 text-center">
              <p className="text-[10px] text-neutral-500 tracking-widest uppercase">
                Powered by <span className="text-[#7C3AED] font-bold">TicketUp</span>
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* Reusable ticket-stub divider with round nibble cutouts */
function TicketDivider() {
  return (
    <div className="relative py-3 flex items-center justify-center">
      {/* Left nibble */}
      <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-4 h-4 bg-[#0b0b0e] rounded-full" />
      {/* Right nibble */}
      <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-4 h-4 bg-[#0b0b0e] rounded-full" />
      {/* Dashed line */}
      <div className="w-full border-t-2 border-dashed border-[#2a2a3d]" />
    </div>
  );
}

/* Info row component */
function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-center bg-[#161621] rounded-lg px-3 py-2.5 border border-[#1e1e2f]">
      <div className="w-6 h-6 flex items-center justify-center mr-3">{icon}</div>
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-bold">{label}</p>
        <p className="text-sm font-semibold text-white mt-0.5">{value}</p>
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────── */
export default function UserDashboard() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isTicketViewOpen, setIsTicketViewOpen] = useState(false);

  const load = () => {
    api.get("/tickets/me").then(({ data }) => setTickets(data));
    api.get("/orders/me").then(({ data }) => setOrders(data));
  };
  useEffect(() => { load(); }, []);

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setIsTicketViewOpen(true);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="user-dashboard">
      <span className="label-eyebrow">Dashboard</span>
      <h1 className="font-display text-4xl sm:text-6xl mt-2 tracking-tighter dark:text-white">Hey, {user?.name?.split(" ")[0]}.</h1>
      <p className="mt-2 text-neutral-600 dark:text-neutral-400">Manage your tickets and orders.</p>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: "Tickets", v: tickets.length, Icon: Ticket },
          { l: "Orders", v: orders.length, Icon: ShoppingBag },
          { l: "Used", v: tickets.filter(t => t.used).length, Icon: Ticket },
        ].map((s, i) => (
          <div key={i} className="stat-card dark:bg-[#111827]">
            <s.Icon size={20} className="text-[#4F46E5]" />
            <p className="font-display text-4xl mt-3 dark:text-white" data-testid={`stat-${s.l}`}>{s.v}</p>
            <p className="text-xs uppercase tracking-widest text-neutral-500 dark:text-neutral-400 mt-1">{s.l}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="tickets" className="mt-12">
        <TabsList className="bg-transparent border-b border-neutral-200 dark:border-neutral-700 rounded-none w-full justify-start gap-2">
          <TabsTrigger value="tickets" data-testid="tab-tickets" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white">My Tickets</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white">Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="mt-6">
          {tickets.length === 0 ? (
            <p className="text-neutral-500 dark:text-neutral-400 py-8" data-testid="no-tickets">No tickets yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tickets.map((t) => (
                <div key={t.id} className="border border-neutral-200 dark:border-neutral-700 dark:bg-[#151c2c] p-4 flex gap-4 rounded-xl shadow-sm hover:shadow-md transition-shadow" data-testid={`ticket-${t.id}`}>
                  <img src={t.qr_data_url} alt="QR" className="w-28 h-28 rounded-lg" />
                  <div className="flex-1">
                    <p className="label-eyebrow dark:text-neutral-400">{t.ticket_type_name}</p>
                    <h3 className="font-display text-lg mt-1 leading-tight dark:text-white">{t.event_title}</h3>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{new Date(t.event_date).toLocaleString()}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{t.venue}</p>
                    <p className="text-xs mt-2 font-mono text-[#4F46E5]">{t.serial_number || `#${t.id.slice(0,8)}`}</p>
                    <div className="mt-3 flex flex-wrap gap-2 items-center">
                      <button
                        onClick={async () => {
                          const ok = await downloadTicketPdf(t.id, t.serial_number);
                          if (ok) toast.success("PDF downloaded"); else toast.error("Download failed");
                        }}
                        className="text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1 hover:text-[#4F46E5] dark:text-neutral-300"
                        data-testid={`download-pdf-${t.id}`}
                      ><DownloadSimple size={14} weight="bold" /> Download PDF</button>
                      <button
                        onClick={() => handleViewTicket(t)}
                        className="text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1 text-[#4F46E5] hover:underline"
                      >
                        <Ticket size={14} weight="bold" /> View Ticket
                      </button>
                      {t.used && <span className="text-xs bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-300 px-2 py-1 rounded-full">Used</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 ? (
            <p className="text-neutral-500 dark:text-neutral-400 py-8" data-testid="no-orders">No orders yet.</p>
          ) : (
            <div className="table-container dark:bg-[#111827]">
              <table className="w-full text-sm" data-testid="orders-table">
                <thead>
                  <tr><th>Order</th><th>Event</th><th>Tickets</th><th>Total</th><th>Status</th></tr>
                </thead>
                <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td className="p-3 font-mono text-xs dark:text-neutral-300">{o.id.slice(0, 8)}</td>
                    <td className="p-3 dark:text-neutral-300">{o.event_title}</td>
                    <td className="p-3 dark:text-neutral-300">{o.items.reduce((a, i) => a + i.quantity, 0)}</td>
                    <td className="p-3 font-bold dark:text-white">৳{o.total.toLocaleString()}</td>
                    <td className="p-3"><span className="text-xs uppercase font-bold text-[#10B981]">{o.payment_status}</span></td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <DigitalTicketView
        ticket={selectedTicket}
        open={isTicketViewOpen}
        onClose={() => { setIsTicketViewOpen(false); setSelectedTicket(null); }}
      />
    </div>
  );
}
